/**
 * Raid danger zones — emitters + Soft≪AA kind taxonomy.
 * Geometry queries live in engine `src/sim/danger-zone.ts`.
 */
import {
  activeDangerZoneAt as engineActiveAt,
  firstEnteredDangerZoneOnSegment as engineFirstEntered,
  pointClearsAllDangerZones as engineClearsAll,
  pointClearsDangerZone as engineClears,
  pointInAnyDangerZone as enginePointInAny,
  pointInDangerZone as enginePointIn,
  projectClearOfDangerZones as engineProject,
  segmentEntersDangerZone as engineSegmentEnters,
  type DangerZoneGeom,
} from "../../sim/danger-zone";
import type { RaidBoss, RaidDanger, RaidUnit } from "./raid-types";
import {
  RaidEnemyTag,
  RaidSkillId,
  RaidZoneKind,
  enemyHasTag,
  enemyIsCombatRelevant,
  type RaidZoneKind as RaidZoneKindId,
} from "./raid-enemy";

export type { DangerZoneGeom };

/** Foresight / pack-AA volume. Shared by boss and skill adds. */
export type RaidDangerZone = DangerZoneGeom & {
  id: string;
  sourceId: number | "boss";
  danger: RaidDanger;
  /**
   * Hit-volume identity. AI goal selection may branch on charge
   * (approach-axis sidestep vs generic floor-corridor exit); other rules
   * still must not read caster leap.phase.
   */
  skillId?: string;
  /**
   * `skill` = T0 dodge/freeze phen. `packAa` = pack chew circles (survive/space only).
   * Default skill when omitted (legacy emitters).
   */
  kind?: RaidZoneKindId;
};

export type CollectDangerOpts = {
  enemies?: readonly RaidUnit[];
  /** When false, skip boss-sourced zones (dormant / not combat-armed). */
  includeBoss?: boolean;
  /** Break stun: skip boss hard zones (soft bodyBroken handled separately). */
  bossBroken?: boolean;
};

function zoneKind(z: RaidDangerZone): RaidZoneKindId {
  return z.kind ?? RaidZoneKind.Skill;
}

/** Skill foresight only — T0 dodge / freeze / instinct phen. */
export function isSkillDangerZone(z: RaidDangerZone): boolean {
  return zoneKind(z) === RaidZoneKind.Skill;
}

/** Pack AA chew circles — not T0 phen. */
export function isPackAaDangerZone(z: RaidDangerZone): boolean {
  return zoneKind(z) === RaidZoneKind.PackAa;
}

type DangerCasterPose = {
  x: number;
  y: number;
  telegraph: RaidBoss["telegraph"];
  leap: RaidBoss["leap"];
};

function pushCasterDangerZones(
  zones: RaidDangerZone[],
  sourceId: number | "boss",
  caster: DangerCasterPose,
): void {
  const leap = caster.leap;
  if (leap) {
    const isCharge = leap.purpose === "charge";
    if (isCharge) {
      const dx = leap.landX - leap.fromX;
      const dy = leap.landY - leap.fromY;
      const len = Math.hypot(dx, dy);
      if (Number.isFinite(len) && len > 1e-3) {
        zones.push({
          id: `${sourceId}:charge`,
          sourceId,
          shape: "line",
          x: leap.fromX,
          y: leap.fromY,
          range: len,
          aimAngle: Math.atan2(dy, dx),
          halfWidth: leap.landRadius,
          danger: leap.danger,
          skillId: RaidSkillId.Charge,
          kind: RaidZoneKind.Skill,
        });
      }
    } else {
      zones.push({
        id: `${sourceId}:leap`,
        sourceId,
        shape: "circle",
        x: leap.landX,
        y: leap.landY,
        range: leap.landRadius,
        danger: leap.danger,
        skillId: RaidSkillId.Leap,
        kind: RaidZoneKind.Skill,
      });
    }
  }
  const tg = caster.telegraph;
  if (tg && tg.remain > 0 && tg.skillId !== RaidSkillId.Charge) {
    const pad = Math.max(0, tg.originPad ?? 0);
    const ox = caster.x + Math.cos(tg.aimAngle) * pad;
    const oy = caster.y + Math.sin(tg.aimAngle) * pad;
    zones.push({
      id: `${sourceId}:${tg.skillId}`,
      sourceId,
      shape: tg.shape,
      x: tg.shape === "line" ? ox : caster.x,
      y: tg.shape === "line" ? oy : caster.y,
      range: tg.range,
      coneDeg: tg.coneDeg,
      aimAngle: tg.aimAngle,
      halfWidth: tg.halfWidth,
      danger: tg.danger,
      skillId: tg.skillId,
      kind: RaidZoneKind.Skill,
    });
  }
}

/**
 * Collect skill DangerZones from boss + any hostile with telegraph/leap.
 * Does **not** include packAa — use {@link collectPackAaZones}.
 */
export function collectRaidDangerZones(
  boss: RaidBoss,
  opts?: CollectDangerOpts,
): RaidDangerZone[] {
  const zones: RaidDangerZone[] = [];
  const includeBoss = opts?.includeBoss !== false;
  if (includeBoss && !opts?.bossBroken) {
    pushCasterDangerZones(zones, "boss", boss);
  }
  const enemies = opts?.enemies;
  if (enemies) {
    for (const e of enemies) {
      if (e.team !== 1 || e.hp <= 0) continue;
      if (e.telegraph == null && e.leap == null) continue;
      pushCasterDangerZones(zones, e.id, e);
    }
  }
  return zones;
}

/**
 * Pack-chew AA circles for combat-relevant enemies tagged packChew.
 */
export function collectPackAaZones(
  enemies: readonly RaidUnit[],
): RaidDangerZone[] {
  const zones: RaidDangerZone[] = [];
  for (const e of enemies) {
    if (!enemyIsCombatRelevant(e) || !e.combatArmed) continue;
    if (!enemyHasTag(e, RaidEnemyTag.PackChew)) continue;
    zones.push({
      id: `${e.id}:packAa`,
      sourceId: e.id,
      shape: "circle",
      x: e.x,
      y: e.y,
      range: e.attackRange,
      danger: "yellow",
      kind: RaidZoneKind.PackAa,
    });
  }
  return zones;
}

/** Feet outside every packAa circle (+eps). Empty pack → clear. */
export function clearOfPackAaZones(
  x: number,
  y: number,
  unitRadius: number,
  packZones: readonly RaidDangerZone[],
  eps = 2,
): boolean {
  const clear = Math.max(0, eps);
  for (const z of packZones) {
    if (!isPackAaDangerZone(z)) continue;
    if (pointInDangerZone(x, y, unitRadius + clear, z)) return false;
  }
  return true;
}

export function pointInDangerZone(
  x: number,
  y: number,
  unitRadius: number,
  z: RaidDangerZone,
): boolean {
  return enginePointIn(x, y, unitRadius, z);
}

export function pointInAnyDangerZone(
  x: number,
  y: number,
  unitRadius: number,
  zones: readonly RaidDangerZone[],
  danger?: RaidDanger,
): boolean {
  return enginePointInAny(x, y, unitRadius, zones, danger);
}

export function unitInDangerZones(
  u: Pick<RaidUnit, "x" | "y" | "radius">,
  zones: readonly RaidDangerZone[],
  danger?: RaidDanger,
): boolean {
  return pointInAnyDangerZone(u.x, u.y, u.radius, zones, danger);
}

export function pointClearsDangerZone(
  x: number,
  y: number,
  unitRadius: number,
  z: RaidDangerZone,
  pad: number,
): boolean {
  return engineClears(x, y, unitRadius, z, pad);
}

export function pointClearsAllDangerZones(
  x: number,
  y: number,
  unitRadius: number,
  zones: readonly RaidDangerZone[],
  pad: number,
  danger?: RaidDanger,
): boolean {
  return engineClearsAll(x, y, unitRadius, zones, pad, danger);
}

export function projectClearOfDangerZones(
  x0: number,
  y0: number,
  unitRadius: number,
  zones: readonly RaidDangerZone[],
  pad = 4,
  danger?: RaidDanger,
): { x: number; y: number } {
  return engineProject(x0, y0, unitRadius, zones, pad, danger);
}

export function firstEnteredDangerZoneOnSegment(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  unitRadius: number,
  zones: readonly RaidDangerZone[],
  steps = 8,
  danger?: RaidDanger,
): RaidDangerZone | null {
  return engineFirstEntered(
    x0,
    y0,
    x1,
    y1,
    unitRadius,
    zones,
    steps,
    danger,
  );
}

export function segmentEntersDangerZone(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  unitRadius: number,
  zones: readonly RaidDangerZone[],
  steps = 8,
  danger?: RaidDanger,
): boolean {
  return engineSegmentEnters(
    x0,
    y0,
    x1,
    y1,
    unitRadius,
    zones,
    steps,
    danger,
  );
}

export function activeDangerZoneAt(
  x: number,
  y: number,
  unitRadius: number,
  zones: readonly RaidDangerZone[],
  danger?: RaidDanger,
): RaidDangerZone | null {
  return engineActiveAt(x, y, unitRadius, zones, danger);
}

export function hasDangerTier(
  zones: readonly RaidDangerZone[],
  danger: RaidDanger,
): boolean {
  return zones.some((z) => z.danger === danger);
}
